import { useEffect, useMemo, useRef, useState } from "react";
import useSWR from "swr";
import { ActionIcon, Badge, Button, Checkbox, Flex, Group, Loader, Pagination, Paper, Divider, SimpleGrid, Stack, Text, TextInput } from "@mantine/core";
import { IconAdjustmentsHorizontal, IconSearch } from "@tabler/icons-react";
import { Link } from "react-router-dom";
import { fetcher } from "../../../../services/fetcher";
import { useDebouncedSearchQuery } from "../../../../common/hooks/useDebouncedSearchQuery/useDebouncedSearchQuery";
import classes from "./CoverPlansExplorer.module.css";

const EXPLORER_SWR_CONFIG = Object.freeze({ keepPreviousData: true });

const BASE_FILTERS = ["Domestic", "International", "Special Packages", "Expeditions", "Trekking"];
const EMPTY_ROWS = [];

const getSearchBlob = (row) =>
  [row?.token, row?.cover_token, row?.title, row?.cover_title, row?.cover_scope, row?.partner_name, row?.currency]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

const getPlanTags = (row) => {
  const source = [row?.title, row?.cover_title, row?.cover_scope, row?.type, row?.adventure_names].filter(Boolean).join(" ").toLowerCase();
  const tags = [];

  if (source.includes("domestic") || source.includes("india")) tags.push("Domestic");
  if (source.includes("international") || source.includes("global") || source.includes("world") || source.includes("nepal")) tags.push("International");
  if (source.includes("special") || source.includes("package")) tags.push("Special Packages");
  if (source.includes("expedition")) tags.push("Expeditions");
  if (source.includes("trek")) tags.push("Trekking");

  return tags;
};

const getPlanHighlights = (row) => {
  const highlights = [];

  if (row?.cover_scope) highlights.push(`${row.cover_scope} coverage`);
  if (row?.partner_name) highlights.push(`Serviced by ${row.partner_name}`);
  if (row?.currency) highlights.push(`Premium currency: ${row.currency}`);
  if (row?.max_duration || row?.duration_days) {
    highlights.push(`Coverage duration up to ${row?.max_duration || row?.duration_days} days`);
  }

  if (highlights.length === 0) {
    highlights.push("Comprehensive medical coverage");
    highlights.push("24/7 support and emergency assistance");
    highlights.push("Baggage and journey interruption benefits");
    highlights.push("Flexible cover options");
  }

  return highlights.slice(0, 4);
};

const CoverPlansExplorer = ({ endpoint }) => {
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const { apiSearchQuery: apiSearch, isSearchPending } =
    useDebouncedSearchQuery(searchTerm);
  const [activeFilters, setActiveFilters] = useState([]);
  const [showAllFilters, setShowAllFilters] = useState(false);
  const [showFilters, setShowFilters] = useState(true);

  const prevApiSearchRef = useRef(apiSearch);
  const searchQueryChanged = prevApiSearchRef.current !== apiSearch;
  const requestPage = searchQueryChanged ? 1 : page;

  if (searchQueryChanged) {
    prevApiSearchRef.current = apiSearch;
  }

  const listUrl = useMemo(() => {
    const params = new URLSearchParams();
    params.set("page", String(requestPage));
    if (apiSearch) params.set("search", apiSearch);

    const joiner = endpoint.includes("?") ? "&" : "?";
    return `${endpoint}${joiner}${params.toString()}`;
  }, [endpoint, requestPage, apiSearch]);

  const swrKey = useMemo(() => {
    if (isSearchPending) return null;
    return listUrl;
  }, [isSearchPending, listUrl]);

  const { data, isLoading, isValidating } = useSWR(swrKey, fetcher, EXPLORER_SWR_CONFIG);
  const rows = useMemo(() => data?.results || EMPTY_ROWS, [data?.results]);
  const totalCount = data?.count || 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / 25));

  useEffect(() => {
    if (requestPage === 1 && page !== 1) {
      setPage(1);
    }
  }, [apiSearch, requestPage, page]);

  const filterOptions = useMemo(() => {
    const inferred = new Set(BASE_FILTERS);
    rows.forEach((row) => getPlanTags(row).forEach((tag) => inferred.add(tag)));
    return Array.from(inferred);
  }, [rows]);

  const visibleFilters = showAllFilters ? filterOptions : filterOptions.slice(0, 5);
  const hiddenFiltersCount = Math.max(0, filterOptions.length - 5);

  const filteredRows = useMemo(() => {
    if (!rows.length) return [];

    const searchValue = apiSearch.toLowerCase();
    return rows.filter((row) => {
      if (searchValue && !getSearchBlob(row).includes(searchValue)) return false;
      if (!activeFilters.length) return true;

      const rowTags = getPlanTags(row);
      return activeFilters.some((filter) => rowTags.includes(filter));
    });
  }, [rows, apiSearch, activeFilters]);

  const toggleFilter = (filterName) => {
    setActiveFilters((prev) =>
      prev.includes(filterName) ? prev.filter((item) => item !== filterName) : [...prev, filterName]
    );
  };

  return (
    <>
      {/* <Flex justify="space-between" align="center" gap="md" wrap="wrap" mb="md">
        <Title order={2} className={classes.pageTitle}>{title}</Title>
        {createAction && createAction}
      </Flex> */}

      <Group gap="sm" mb="md">
        <TextInput
          className={classes.searchInput}
          placeholder="Search here..."
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.currentTarget.value)}
          leftSection={<IconSearch size={16} />}
          leftSectionWidth={36}
        />
        <ActionIcon
          size={42}
          radius="md"
          variant="light"
          className={classes.filterIconBtn}
          onClick={() => setShowFilters((prev) => !prev)}
        >
          <IconAdjustmentsHorizontal size={18} />
        </ActionIcon>
      </Group>
      <Paper radius="lg" p="lg">
        {showFilters && (
          <Paper radius="md" p="sm" >
            <Group gap="lg">
              {visibleFilters.map((item) => (
                <Checkbox
                  key={item}
                  label={item}
                  checked={activeFilters.includes(item)}
                  onChange={() => toggleFilter(item)}
                  classNames={{ label: classes.filterLabel }}
                />
              ))}
              {hiddenFiltersCount > 0 && (
                <Button
                  variant="subtle"
                  className={classes.moreBtn}
                  onClick={() => setShowAllFilters((prev) => !prev)}
                >
                  {showAllFilters ? "Show less" : `+ ${hiddenFiltersCount} more`}
                </Button>
              )}
            </Group>
            <Divider size={2} mt="xl"
              styles={{
                root: {
                  border: "none",
                  height: 2,
                  background:
                    "linear-gradient(270deg, #0053A8 0%, #033F7C 100%)",
                  borderRadius: 8,
                  opacity: 0.15,
                },
              }}
            />   
          </Paper>
          
        )}

        {(!data && isLoading) || isValidating ? (
          <Flex justify="center" py="xl">
            <Loader color="blue" />
          </Flex>
        ) : (
          <Stack mt="md" gap="md">
            <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="md">
              {filteredRows.map((row) => (
                <Paper key={row.id}  bg="#edf4fb" radius="md" p="md">
                  <Flex justify="space-between" align="flex-start" gap="sm">
                    <Text component={Link} to={`${row.id}`} fw={500} c="#092C4C">
                      {row.title || row.cover_title || row.token || row.cover_token || "Plan"}
                    </Text>
                    {row?.is_expired ? (
                      <Badge color="gray" radius="xl" size="lg">Expired</Badge>
                    ) : (
                      <Badge color="#69D27A" radius="xl" size="lg">Active</Badge>
                    )}
                  </Flex>

                  <Text c="#6C94B4" fw={400} mt="xs" mb="sm">
                    Comprehensive adventure protection plan
                  </Text>

                  <ul className={classes.points}>
                    {getPlanHighlights(row).map((point) => (
                      <li key={`${row.id}-${point}`}>{point}</li>
                    ))}
                  </ul>
                </Paper>
              ))}
            </SimpleGrid>

            {!filteredRows.length && (
              <Text ta="center" className={classes.emptyText}>No plans found for current filters.</Text>
            )}
          </Stack>
        )}

        <Flex justify="space-between" align="center" mt="lg" px="xs" wrap="wrap" gap="sm">
          <Text size="sm" c="dimmed">
            Showing {filteredRows.length} of {totalCount} plans
          </Text>
          <Pagination page={page} onChange={setPage} total={totalPages} siblings={1} />
        </Flex>
      </Paper>
    </>
  );
};

export default CoverPlansExplorer;